import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useState, useMemo } from "react";
import { SectionHeading, RarityPill } from "@/components/Atoms";
import { TinyCard, TINY_GRID, CardHoverPreview } from "@/components/Card3D";
import { PaginationControls } from "@/components/PaginationControls";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Layers, Search, Copy, Sparkles, Coins, Crown, Gem, TrendingUp, Star, Skull, Tag as TagIcon } from "lucide-react";
import { motion } from "framer-motion";
import { EmptyState } from "@/components/Atoms";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { copyText } from "@/lib/clipboard";

const RARITIES = ["all", "common", "uncommon", "rare", "epic", "legendary", "goddess"];
const RARITY_ORDER = ["common", "uncommon", "rare", "epic", "legendary", "goddess"];
// Mirrors backend RARITY_BASE_VALUES — used for an at-a-glance portfolio value.
const RARITY_META = {
  common: { color: "#9ca3af", value: 100 },
  uncommon: { color: "#34d399", value: 250 },
  rare: { color: "#22d3ee", value: 600 },
  epic: { color: "#a78bfa", value: 1500 },
  legendary: { color: "#fbbf24", value: 4000 },
  goddess: { color: "#f472b6", value: 10000 },
};

/* ── Donut chart for collection-by-rarity (replaces the hard-to-read bar) ── */
function RarityDonut({ counts, total, active, onSlice }) {
  const [hover, setHover] = useState(null);
  const size = 200, stroke = 30, r = (size - stroke) / 2, circ = 2 * Math.PI * r;
  const segs = [];
  let acc = 0;
  RARITY_ORDER.forEach((rarity) => {
    const n = counts[rarity] || 0;
    if (!n) return;
    const frac = n / total;
    segs.push({ rarity, n, frac, offset: acc });
    acc += frac;
  });
  const focusKey = hover || (active && active !== "all" ? active : null);
  const focus = focusKey ? { rarity: focusKey, n: counts[focusKey] || 0 } : null;
  return (
    <div className="relative" style={{ width: size, height: size }} data-testid="collection-rarity-donut">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={stroke} style={{ pointerEvents: "none" }} />
        {segs.map((s) => {
          const dim = focusKey && focusKey !== s.rarity;
          return (
            <circle
              key={s.rarity}
              cx={size / 2} cy={size / 2} r={r} fill="none"
              stroke={RARITY_META[s.rarity].color}
              strokeWidth={focusKey === s.rarity ? stroke + 6 : stroke}
              strokeDasharray={`${s.frac * circ} ${circ - s.frac * circ}`}
              strokeDashoffset={-s.offset * circ}
              strokeLinecap="butt"
              className="cursor-pointer transition-all duration-300"
              style={{ opacity: dim ? 0.25 : 1, filter: focusKey === s.rarity ? `drop-shadow(0 0 8px ${RARITY_META[s.rarity].color})` : "none" }}
              onMouseEnter={() => setHover(s.rarity)}
              onMouseLeave={() => setHover(null)}
              onClick={() => onSlice(s.rarity)}
              data-testid={`collection-rarity-slice-${s.rarity}`}
            />
          );
        })}
      </svg>
      {/* center readout */}
      <div className="absolute inset-0 grid place-content-center text-center pointer-events-none">
        {focus ? (
          <>
            <div className="font-display text-3xl tabular-nums" style={{ color: RARITY_META[focus.rarity].color }}>{focus.n}</div>
            <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/55 mt-0.5">{focus.rarity}</div>
            <div className="text-[10px] text-white/40 mt-0.5">{Math.round((focus.n / total) * 100)}%</div>
          </>
        ) : (
          <>
            <div className="font-display text-4xl text-white tabular-nums">{fmt(total)}</div>
            <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/45 mt-0.5">cards</div>
          </>
        )}
      </div>
    </div>
  );
}


function fmt(n) {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, "") + "K";
  return n.toLocaleString();
}

function StatTile({ label, value, sub, icon: Icon, accent, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
      className="hud-pill rounded-2xl p-4 relative overflow-hidden group"
      style={{ borderLeft: `2px solid ${accent}66` }}
      data-testid={`collection-stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    >
      <div className="absolute inset-0 holo-scan opacity-[0.08] pointer-events-none" />
      <div
        className="absolute -right-6 -top-6 w-20 h-20 rounded-full blur-2xl opacity-30 transition-opacity group-hover:opacity-50"
        style={{ background: accent }}
      />
      <div className="relative flex items-center justify-between">
        <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/45">{label}</div>
        <Icon className="h-4 w-4" style={{ color: accent }} />
      </div>
      <div className="relative font-display text-3xl sm:text-4xl text-white mt-2 tracking-[0.02em]">{value}</div>
      {sub && <div className="relative text-[10px] text-white/45 mt-1 font-mono truncate">{sub}</div>}
    </motion.div>
  );
}

function EditionsDialog({ group, onClose }) {
  const navigate = useNavigate();
  if (!group) return null;
  const sorted = [...group.cards].sort((a, b) => (a.edition_number || 0) - (b.edition_number || 0));
  const copySerial = async (serial) => { (await copyText(serial)) ? toast.success("Serial copied") : toast.error("Couldn't copy"); };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[hsl(222_22%_8%)] border-white/10 max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-white tracking-[0.06em] flex items-center gap-2">
            {group.cards[0].title}
          </DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-2 -mt-1 mb-2">
          <RarityPill rarity={group.cards[0].rarity} />
          <span className="font-mono text-[11px] text-white/50 uppercase tracking-[0.2em]">{sorted.length} edition{sorted.length === 1 ? "" : "s"} owned</span>
        </div>
        <div className="space-y-2" data-testid="editions-list">
          {sorted.map((c) => (
            <CardHoverPreview key={c.id} card={c} side="right">
              <div
                role="button"
                tabIndex={0}
                className="hud-pill rounded-xl p-3 flex items-center justify-between gap-3 cursor-pointer hover:bg-white/5 hover:border-cyan-400/30 transition-colors duration-150 outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/50"
                data-testid={`edition-row-${c.id}`}
                onClick={() => { onClose(); navigate(`/card/${c.id}`); }}
                onKeyDown={(e) => { if (e.key === "Enter") { onClose(); navigate(`/card/${c.id}`); } }}
              >
                <div className="min-w-0">
                  <div className="font-mono text-sm text-white">Edition #{c.edition_number ?? "—"}</div>
                  <button
                    onClick={(e) => { e.stopPropagation(); copySerial(c.serial); }}
                    className="font-mono text-[11px] text-cyan-300/80 inline-flex items-center gap-1 mt-0.5 hover:text-cyan-200"
                    data-testid={`edition-copy-${c.id}`}
                  >
                    <Copy className="h-3 w-3" /> {c.serial}
                  </button>
                </div>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 shrink-0">Hover · Tap</span>
              </div>
            </CardHoverPreview>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function CollectionPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");
  const [levelFilter, setLevelFilter] = useState("all");
  const [favOnly, setFavOnly] = useState(false);
  const [search, setSearch] = useState("");
  const qc = useQueryClient();
  const favMutation = useMutation({
    mutationFn: async ({ id, favorite }) => (await api.post(`/cards/${id}/favorite`, { favorite })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["my-cards"] }),
    onError: () => toast.error("Couldn't update favourite"),
  });
  const toggleFav = (card, e) => {
    e?.stopPropagation();
    favMutation.mutate({ id: card.id, favorite: !card.favorite });
  };
  const [activeGroup, setActiveGroup] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const { data: cards, isLoading } = useQuery({
    queryKey: ["my-cards", search],
    queryFn: async () => {
      const p = new URLSearchParams();
      if (search) p.set("q", search);
      return (await api.get(`/cards/mine?${p}`)).data;
    },
  });

  const matchLevel = (c) => {
    const lv = c.level || 1;
    switch (levelFilter) {
      case "all": return true;
      case "1": return lv === 1;
      case "2+": return lv >= 2;
      case "5+": return lv >= 5;
      case "max": return lv >= 10;
      default: return true;
    }
  };
  const filtered = (cards || []).filter((c) => (filter === "all" || c.rarity === filter) && matchLevel(c) && (!favOnly || c.favorite));
  const counts = (cards || []).reduce((acc, c) => { acc[c.rarity] = (acc[c.rarity] || 0) + 1; return acc; }, {});

  // Group identical cards (same template) into stacks. Cards without a template
  // id fall back to their own id (always a stack of 1).
  const groups = useMemo(() => {
    const map = new Map();
    for (const c of filtered) {
      // Shamed cards never stack — each shows as its own locked placeholder tile.
      const key = c.shamed ? `shamed-${c.id}` : (c.template_id || c.id);
      if (!map.has(key)) map.set(key, { key, cards: [] });
      map.get(key).cards.push(c);
    }
    // keep editions newest-first within a stack, groups ordered by newest card
    return Array.from(map.values()).map((g) => ({
      ...g,
      cards: [...g.cards].sort((a, b) => (b.edition_number || 0) - (a.edition_number || 0)),
    }));
  }, [filtered]);

  const totalCards = (cards || []).length;
  const uniqueCount = new Set((cards || []).map((c) => c.template_id || c.id)).size;

  // Portfolio insights (whole collection, independent of active filter).
  // Est. value = the IMMEDIATE shop sale value (quick-sell returns 1/10 of base value).
  const estValue = useMemo(
    () => (cards || []).reduce((s, c) => s + Math.floor((RARITY_META[c.rarity]?.value || 0) / 10), 0),
    [cards]
  );
  // Tags across the whole collection, ranked by how many cards carry each.
  const tagCounts = useMemo(() => {
    const m = new Map();
    for (const c of cards || []) {
      for (const t of c.tags || []) {
        const key = String(t).trim();
        if (key) m.set(key, (m.get(key) || 0) + 1);
      }
    }
    return Array.from(m.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [cards]);
  const tiersOwned = RARITY_ORDER.filter((r) => counts[r]).length;
  const topTier = useMemo(() => [...RARITY_ORDER].reverse().find((r) => counts[r]), [counts]);

  const totalPages = Math.max(1, Math.ceil(groups.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const pagedGroups = groups.slice(safePage * pageSize, (safePage + 1) * pageSize);

  const onOpen = (group) => {
    if (group.cards.length > 1) setActiveGroup(group);
    else navigate(`/card/${group.cards[0].id}`);
  };

  const hasCards = totalCards > 0;

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-6 py-5 sm:py-10">
      <SectionHeading eyebrow="Owned" title="YOUR PLAYDEN" description="Cards minted to you. Duplicates stack together — tap a stack to flip through your editions." />

      {/* ── Portfolio dashboard ───────────────────────────────────── */}
      {hasCards && (
        <div className="mb-8 space-y-3" data-testid="collection-dashboard">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatTile label="Total Cards" value={fmt(totalCards)} icon={Layers} accent="#22d3ee" sub={`${uniqueCount} unique design${uniqueCount === 1 ? "" : "s"}`} delay={0} />
            <StatTile label="Unique Designs" value={fmt(uniqueCount)} icon={Sparkles} accent="#a78bfa" sub={`${totalCards - uniqueCount} duplicate${totalCards - uniqueCount === 1 ? "" : "s"}`} delay={0.06} />
            <StatTile label="Est. Value" value={`${fmt(estValue)}c`} icon={Coins} accent="#fbbf24" sub="immediate shop sale value" delay={0.12} />
            <StatTile label="Tiers Collected" value={`${tiersOwned}/6`} icon={Crown} accent="#f472b6" sub={topTier ? `Top tier: ${topTier}` : "—"} delay={0.18} />
          </div>

          {/* Rarity distribution */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.22, ease: [0.22, 1, 0.36, 1] }}
            className="hud-pill rounded-2xl p-5 relative overflow-hidden"
            data-testid="collection-rarity-breakdown"
          >
            <div className="absolute inset-0 holo-scan opacity-[0.06] pointer-events-none" />
            <div className="relative flex items-center gap-2 mb-4">
              <Gem className="h-4 w-4 text-cyan-300" />
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/45">Collection by rarity</div>
            </div>

            {/* Donut chart + clickable legend chips */}
            <div className="relative flex flex-col sm:flex-row items-center gap-6">
              <RarityDonut
                counts={counts}
                total={totalCards}
                active={filter}
                onSlice={(r) => { setFilter(filter === r ? "all" : r); setPage(0); }}
              />

              {/* Clickable rarity chips (double as quick filters / legend) */}
              <div className="relative flex flex-wrap gap-2 flex-1">
                {RARITY_ORDER.map((r) => {
                  const n = counts[r] || 0;
                  const active = filter === r;
                  return (
                    <button
                      key={r}
                      onClick={() => { setFilter(active ? "all" : r); setPage(0); }}
                      disabled={n === 0}
                      data-testid={`collection-rarity-chip-${r}`}
                      className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 border text-[11px] font-mono uppercase tracking-[0.12em] transition-all ${
                        n === 0 ? "opacity-30 cursor-not-allowed border-white/10 text-white/40" : "hover:-translate-y-px"
                      } ${active ? "text-white" : "text-white/70"}`}
                      style={{
                        borderColor: active ? `${RARITY_META[r].color}` : `${RARITY_META[r].color}44`,
                        background: active ? `${RARITY_META[r].color}26` : `${RARITY_META[r].color}10`,
                        boxShadow: active ? `0 0 16px ${RARITY_META[r].color}55` : "none",
                      }}
                    >
                      <span className="h-2 w-2 rounded-full" style={{ background: RARITY_META[r].color }} />
                      {r}
                      <span className="text-white font-semibold tabular-nums">{n}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </motion.div>

          {/* Tags across the collection, ranked by how many cards carry each */}
          {tagCounts.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.28, ease: [0.22, 1, 0.36, 1] }}
              className="hud-pill rounded-2xl p-5"
              data-testid="collection-tags"
            >
              <div className="flex items-center gap-2 mb-3">
                <TagIcon className="h-4 w-4 text-emerald-300" />
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/45">Tags by count</div>
              </div>
              <div className="flex flex-wrap gap-2">
                {tagCounts.map(([tag, n], i) => (
                  <button
                    key={tag}
                    data-testid={`collection-tag-${tag}`}
                    onClick={() => { setSearch(tag); setPage(0); }}
                    title={`${n} card${n === 1 ? "" : "s"} tagged "${tag}" — click to filter`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/25 bg-emerald-400/[0.06] px-3 py-1.5 text-[11px] font-mono text-emerald-200/90 hover:border-emerald-400/50 hover:-translate-y-px transition-all"
                    style={{ opacity: Math.max(0.55, 1 - i * 0.03) }}
                  >
                    <span className="lowercase tracking-[0.04em]">{tag}</span>
                    <span className="rounded-full bg-emerald-400/20 px-1.5 text-emerald-100 tabular-nums">{n}</span>
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      )}

      {/* ── Filters + search ──────────────────────────────────────── */}
      <div className="flex flex-col lg:flex-row lg:flex-wrap lg:items-center lg:justify-between gap-3 mb-6">
        <div className="-mx-1 overflow-x-auto lg:overflow-visible">
          <Tabs value={filter} onValueChange={(v) => { setFilter(v); setPage(0); }}>
            <TabsList className="bg-[hsl(220_18%_10%)] border border-white/10 flex flex-nowrap">
              {RARITIES.map((r) => (
                <TabsTrigger key={r} value={r} data-testid={`collection-filter-${r}`} className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-white/60 uppercase tracking-[0.2em] text-[10px] whitespace-nowrap">
                  {r}{r !== "all" && counts[r] ? ` (${counts[r]})` : ""}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
        <div className="flex items-center gap-3 w-full lg:w-auto">
          <button
            data-testid="collection-fav-filter"
            onClick={() => { setFavOnly((v) => !v); setPage(0); }}
            className={`h-10 px-3 rounded-md border flex items-center gap-1.5 text-sm transition-colors shrink-0 ${favOnly ? "border-amber-400/60 bg-amber-400/15 text-amber-200" : "border-white/10 text-white/60 hover:text-white"}`}
          >
            <Star className={`h-4 w-4 ${favOnly ? "fill-amber-300 text-amber-300" : ""}`} /> Favorites
          </button>
          <Select value={levelFilter} onValueChange={(v) => { setLevelFilter(v); setPage(0); }}>
            <SelectTrigger className="w-[130px] bg-[hsl(220_18%_12%)] border-white/10 text-white/80" data-testid="collection-level-filter">
              <TrendingUp className="h-3.5 w-3.5 text-emerald-300 mr-1" />
              <SelectValue placeholder="Level" />
            </SelectTrigger>
            <SelectContent className="bg-[hsl(222_22%_8%)] border-white/10 text-white">
              <SelectItem value="all" data-testid="level-opt-all">All levels</SelectItem>
              <SelectItem value="1" data-testid="level-opt-1">Level 1</SelectItem>
              <SelectItem value="2+" data-testid="level-opt-2">Level 2+</SelectItem>
              <SelectItem value="5+" data-testid="level-opt-5">Level 5+</SelectItem>
              <SelectItem value="max" data-testid="level-opt-max">Level 10 (max)</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative flex-1 lg:w-72">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
            <Input
              data-testid="collection-search-input"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              placeholder="Search title, serial or tag…"
              className="pl-9 bg-[hsl(220_18%_12%)] border-white/10"
            />
          </div>
          <div className="font-mono text-xs text-white/40 whitespace-nowrap" data-testid="collection-counts">
            {uniqueCount} unique · {totalCards} total
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center text-white/40 py-12">Loading vault…</div>
      ) : groups.length === 0 ? (
        hasCards ? (
          <div className="hud-pill rounded-2xl p-10 text-center text-white/50" data-testid="collection-no-match">
            No {filter !== "all" ? filter : ""} cards match{search ? ` "${search}"` : ""}.
          </div>
        ) : (
          <EmptyState icon={Layers} title="PLAYDEN IS EMPTY" description="Acquire cards in the store, redeem codes, or open packs to start your collection." ctaLabel="Visit Store" ctaTo="/store" />
        )
      ) : (
        <>
          <div className={TINY_GRID}>
            {pagedGroups.map((g) => {
              const c0 = g.cards[0];
              if (c0.shamed) {
                return (
                  <TinyCard
                    key={g.key}
                    card={c0}
                    idKey={g.key}
                    onClick={() => navigate(`/card/${c0.id}`)}
                    footer={
                      <div className="mt-1 px-0.5">
                        <div className="font-mono text-[9px] text-white/60 truncate">{c0.title}</div>
                        <div className="font-mono text-[9px] text-purple-300 flex items-center gap-1" data-testid={`shamed-tag-${c0.id}`}>🍅 SHAMED · {c0.shame_seconds_left}s</div>
                      </div>
                    }
                  />
                );
              }
              const isNew = g.cards.some((c) => c.is_new);
              return (
                <div key={g.key} className="relative group/fav">
                  <TinyCard card={c0} count={g.cards.length} idKey={g.key} onClick={() => onOpen(g)} />
                  <button
                    data-testid={`fav-toggle-${c0.id}`}
                    onClick={(e) => toggleFav(c0, e)}
                    className={`absolute top-1.5 left-1.5 z-10 h-7 w-7 grid place-items-center rounded-full backdrop-blur-sm border transition-all ${c0.favorite ? "bg-amber-400/25 border-amber-400/60 opacity-100" : "bg-black/50 border-white/15 opacity-0 group-hover/fav:opacity-100"}`}
                    title={c0.favorite ? "Unfavourite" : "Favourite"}
                  >
                    <Star className={`h-3.5 w-3.5 ${c0.favorite ? "fill-amber-300 text-amber-300" : "text-white/80"}`} />
                  </button>
                  {isNew && (
                    <span className="absolute top-1.5 right-1.5 z-20 px-1.5 py-0.5 rounded font-mono text-[8px] font-bold uppercase tracking-[0.2em] bg-emerald-400 text-black shadow-[0_0_10px_rgba(52,211,153,0.8)] animate-pulse" data-testid={`card-new-badge-${c0.id}`}>
                      New
                    </span>
                  )}
                  {c0.won_in_cavern && (
                    <span className={`absolute ${isNew ? "top-9" : "top-1.5"} right-1.5 z-10 h-6 px-1.5 inline-flex items-center gap-0.5 rounded-full bg-purple-500/25 border border-purple-400/50 text-[9px] font-mono text-purple-100`} title="Cavern Mint (+5% all stats)" data-testid={`cavern-badge-${c0.id}`}>
                      <Skull className="h-3 w-3" /> Cavern
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          <PaginationControls
            page={safePage}
            total={groups.length}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(s) => { setPageSize(s); setPage(0); }}
            testidPrefix="collection"
          />
        </>
      )}

      {activeGroup && <EditionsDialog group={activeGroup} onClose={() => setActiveGroup(null)} />}
    </div>
  );
}
